import { defaultBusinessWorkspace, type BusinessWorkspace } from './types';

const WORKSPACE_KEY = 'business-calculator-kr:business-workspace:v1';

function nonNegative(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : fallback;
}

function percent(value: unknown, fallback: number): number {
  const parsed = nonNegative(value, fallback);
  return Math.min(parsed, 100);
}

export function loadBusinessWorkspace(): BusinessWorkspace {
  if (typeof window === 'undefined') return defaultBusinessWorkspace;

  try {
    const raw = window.localStorage.getItem(WORKSPACE_KEY);
    if (!raw) return defaultBusinessWorkspace;
    const parsed = JSON.parse(raw) as Partial<BusinessWorkspace>;
    return {
      revenue: nonNegative(parsed.revenue, defaultBusinessWorkspace.revenue),
      directCost: nonNegative(parsed.directCost, defaultBusinessWorkspace.directCost),
      variableCost: nonNegative(parsed.variableCost, defaultBusinessWorkspace.variableCost),
      fixedCost: nonNegative(parsed.fixedCost, defaultBusinessWorkspace.fixedCost),
      unitPrice: nonNegative(parsed.unitPrice, defaultBusinessWorkspace.unitPrice),
      unitCost: nonNegative(parsed.unitCost, defaultBusinessWorkspace.unitCost),
      unitOtherCost: nonNegative(parsed.unitOtherCost, defaultBusinessWorkspace.unitOtherCost),
      feeRate: percent(parsed.feeRate, defaultBusinessWorkspace.feeRate),
      units: nonNegative(parsed.units, defaultBusinessWorkspace.units),
      targetMarginRate: percent(parsed.targetMarginRate, defaultBusinessWorkspace.targetMarginRate),
      targetProfit: nonNegative(parsed.targetProfit, defaultBusinessWorkspace.targetProfit),
      cashBalance: nonNegative(parsed.cashBalance, defaultBusinessWorkspace.cashBalance),
      adSpend: nonNegative(parsed.adSpend, defaultBusinessWorkspace.adSpend),
      attributedRevenue: nonNegative(parsed.attributedRevenue, defaultBusinessWorkspace.attributedRevenue),
      grossMarginRate: percent(parsed.grossMarginRate, defaultBusinessWorkspace.grossMarginRate),
      updatedAt: typeof parsed.updatedAt === 'string' ? parsed.updatedAt : null,
    };
  } catch {
    return defaultBusinessWorkspace;
  }
}

export function saveBusinessWorkspace(workspace: BusinessWorkspace): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(WORKSPACE_KEY, JSON.stringify({ ...workspace, updatedAt: new Date().toISOString() }));
}

export function resetBusinessWorkspace(): BusinessWorkspace {
  if (typeof window !== 'undefined') window.localStorage.removeItem(WORKSPACE_KEY);
  return defaultBusinessWorkspace;
}
